/**
 * Client-side encryption envelope for headless portal credentials (OLIO-2906).
 *
 * The password's entire journey: this module encrypts it IN THE BROWSER under the
 * integration platform's KMS public key, and only the integration platform's
 * headless worker (via kms:Decrypt) can ever recover it. olio-api relays the
 * resulting envelope without being able to read it — no Olio server is in the
 * plaintext set.
 *
 * Mirrors olio-integration's app/util/portal_credential_envelope.py exactly:
 *   - AES-256-GCM data key encrypts the JSON payload (12-byte IV, tag appended —
 *     WebCrypto's native convention)
 *   - the data key is wrapped with RSA-OAEP-SHA-256 under the fetched public key
 *   - the envelope records the KMS key id that wrapped it, which is what makes
 *     later per-client keys a config change instead of a migration
 *
 * The save endpoint rejects anything that does not parse as this exact shape, so
 * a bug that skipped encryption would fail loudly rather than store plaintext.
 */

export interface CredentialEnvelope {
  v: 1;
  key_id: string;
  wrapped_key: string;
  iv: string;
  ciphertext: string;
}

export interface PortalCredentialPayload {
  username: string;
  password: string;
}

const DATA_KEY_BITS = 256;
const IV_BYTES = 12;

function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const array = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  array.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    array[i] = binary.charCodeAt(i);
  }
  return array;
}

/**
 * Envelope-encrypt portal credentials under the platform public key.
 *
 * @param payload   the credentials — never touches the network unencrypted
 * @param publicKeySpkiBase64  base64 SPKI DER from GET /api/portal_credentials/public_key
 * @param keyId     the KMS key id returned alongside the public key
 */
export async function encryptCredentialEnvelope(
  payload: PortalCredentialPayload,
  publicKeySpkiBase64: string,
  keyId: string
): Promise<CredentialEnvelope> {
  const subtle = window.crypto.subtle;

  const publicKey = await subtle.importKey(
    'spki',
    fromBase64(publicKeySpkiBase64),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );

  const dataKey = await subtle.generateKey({ name: 'AES-GCM', length: DATA_KEY_BITS }, true, ['encrypt']);
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_BYTES));

  const ciphertext = await subtle.encrypt(
    { name: 'AES-GCM', iv },
    dataKey,
    new TextEncoder().encode(JSON.stringify(payload))
  );

  const rawDataKey = await subtle.exportKey('raw', dataKey);
  const wrappedKey = await subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, rawDataKey);

  return {
    v: 1,
    key_id: keyId,
    wrapped_key: toBase64(wrappedKey),
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
  };
}
